declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
}

export interface FacebookUser {
  id: string;
  name: string;
  email?: string;
  picture?: {
    data: {
      url: string;
    };
  };
}

class FacebookService {
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  init(appId: string): Promise<void> {
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve) => {
      // Load Facebook SDK
      window.fbAsyncInit = () => {
        window.FB.init({
          appId: appId || '3211801755664069', // biig.dev Facebook App ID
          cookie: true,
          xfbml: true,
          version: 'v18.0'
        });

        window.FB.AppEvents.logPageView();
        this.initialized = true;
        resolve();
      };

      // Load the SDK asynchronously
      if (!document.getElementById('facebook-jssdk')) {
        const script = document.createElement('script');
        script.id = 'facebook-jssdk';
        script.src = 'https://connect.facebook.net/en_US/sdk.js';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      } else if (window.FB) {
        // SDK already loaded
        this.initialized = true;
        resolve();
      }
    });

    return this.initPromise;
  }

  async login(): Promise<FacebookUser | null> {
    if (!this.initialized) {
      throw new Error('Facebook SDK not initialized');
    }

    return new Promise((resolve) => {
      window.FB.login((response: any) => {
        if (response.authResponse) {
          // Get user data
          window.FB.api('/me', { fields: 'id,name,email,picture.width(200).height(200)' }, (user: FacebookUser) => {
            resolve(user);
          });
        } else {
          resolve(null);
        }
      }, { scope: 'public_profile,email' });
    });
  }

  async logout(): Promise<void> {
    if (!this.initialized) return;

    return new Promise((resolve) => {
      window.FB.logout(() => {
        resolve();
      });
    });
  }

  async getLoginStatus(): Promise<{ isLoggedIn: boolean; user?: FacebookUser }> {
    if (!this.initialized) {
      return { isLoggedIn: false };
    }

    return new Promise((resolve) => {
      window.FB.getLoginStatus((response: any) => {
        if (response.status === 'connected') {
          window.FB.api('/me', { fields: 'id,name,email,picture.width(200).height(200)' }, (user: FacebookUser) => {
            resolve({ isLoggedIn: true, user });
          });
        } else {
          resolve({ isLoggedIn: false });
        }
      });
    });
  }
}

export const facebookService = new FacebookService();